import { listPublishedEssaysOptions } from "@/actions/essay/handler";
import { type EssayMetadata } from "@/components/admin/essayList";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { seo } from "@/utils/seo";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Activity,
  Calculator,
  Droplets,
  Dumbbell,
  Ruler,
  TrendingUp,
} from "lucide-react";

export const Route = createFileRoute("/_app/essay/")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(listPublishedEssaysOptions());
  },
  head: () => ({
    meta: seo({
      title: "Bilgi Kütüphanesi ve Hesaplayıcılar | AtalayTheCoach",
      description:
        "Powerlifting, güç sporları, beslenme ve antrenman üzerine eğitici blog yazıları ve performans hesaplayıcıları.",
      canonical: "https://atalaythecoach.com/essay",
    }),
  }),
  component: EssayListPageComponent,
});

function EssayListPageComponent() {
  const essaysQuery = useSuspenseQuery(listPublishedEssaysOptions());
  const essays = (essaysQuery.data.body?.essays || []) as EssayMetadata[];

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  return (
    <div className="min-h-screen w-full bg-background pt-28 text-foreground sm:pt-32">
      <main className="container mx-auto max-w-4xl px-4">
        <header className="mb-12 border-b pb-6">
          <h1 className="font-extrabold text-4xl tracking-tight sm:text-5xl">
            İçerikler ve Hesaplayıcılar
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Spor, beslenme ve performans üzerine yazılar, rehberler ve araçlar.
          </p>
        </header>

        <div className="space-y-12">
          {/* Hesaplayıcılar Bölümü */}
          <section>
            <h2 className="mb-6 flex items-center gap-2 font-bold text-2xl">
              <Calculator className="h-6 w-6" />
              Fitness Hesaplayıcıları
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Link to="/essay/1rm-calculator" className="group">
                <Card className="h-full transition-colors hover:border-primary">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg group-hover:text-primary">
                      <Dumbbell className="h-5 w-5" />
                      1RM Hesaplayıcı
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Tek tekrar maksimum hesaplayın. Squat, bench press,
                      deadlift için güvenilir 1RM hesaplama aracı.
                    </p>
                    <div className="mt-3 flex items-center text-primary text-xs">
                      <TrendingUp className="mr-1 h-3 w-3" />
                      Powerlifting Aracı
                    </div>
                  </CardContent>
                </Card>
              </Link>

              {/* Placeholder for future calculators */}
              <Link to="/essay/calori-calculator" className="group">
                <Card className="h-full transition-colors hover:border-primary">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg group-hover:text-primary">
                      <Calculator className="h-5 w-5" />
                      Kalori Hesaplayıcı
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Günlük kalori ihtiyacınızı hesaplayın. TDEE ve makro besin
                      dağılımı.
                    </p>
                    <div className="mt-3 flex items-center text-primary text-xs">
                      <TrendingUp className="mr-1 h-3 w-3" />
                      TDEE Hesaplayıcı
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/essay/wilks-calculator" className="group">
                <Card className="h-full transition-colors hover:border-primary">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg group-hover:text-primary">
                      <TrendingUp className="h-5 w-5" />
                      Wilks Skoru
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Powerlifting performansınızı karşılaştırın. Wilks puanı
                      hesaplama aracı.
                    </p>
                    <div className="mt-3 flex items-center text-primary text-xs">
                      <TrendingUp className="mr-1 h-3 w-3" />
                      Powerlifting Aracı
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/essay/body-fat-calculator" className="group">
                <Card className="h-full transition-colors hover:border-primary">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg group-hover:text-primary">
                      <Ruler className="h-5 w-5" />
                      Body Fat Hesaplayıcı
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Vücut yağ oranınızı 4 farklı yöntemle hesaplayın. Kaliper,
                      çevre ölçümleri ve BMI bazlı.
                    </p>
                    <div className="mt-3 flex items-center text-primary text-xs">
                      <TrendingUp className="mr-1 h-3 w-3" />
                      Vücut Kompozisyonu
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/essay/ffmi-calculator" className="group">
                <Card className="h-full transition-colors hover:border-primary">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg group-hover:text-primary">
                      <Activity className="h-5 w-5" />
                      FFMI Hesaplayıcı
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Fat-Free Mass Index ile kas kütlenizi analiz edin. Doğal
                      potansiyelinizi keşfedin.
                    </p>
                    <div className="mt-3 flex items-center text-primary text-xs">
                      <TrendingUp className="mr-1 h-3 w-3" />
                      Kas Kütlesi Analizi
                    </div>
                  </CardContent>
                </Card>
              </Link>

              <Link to="/essay/water-calculator" className="group">
                <Card className="h-full transition-colors hover:border-primary">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg group-hover:text-primary">
                      <Droplets className="h-5 w-5" />
                      Su İhtiyacı Hesaplayıcı
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      Kişiselleştirilmiş günlük su ihtiyacınızı hesaplayın.
                      Aktivite, iklim ve özel durumlar dahil.
                    </p>
                    <div className="mt-3 flex items-center text-primary text-xs">
                      <TrendingUp className="mr-1 h-3 w-3" />
                      Hidrasyon Rehberi
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </section>

          {/* Makaleler Bölümü */}
          <section>
            <h2 className="mb-6 font-bold text-2xl">Son Yazılar</h2>
            {essays.length === 0 ? (
              <p className="text-center text-muted-foreground">
                Henüz yayınlanmış essay bulunamadı.
              </p>
            ) : (
              <div className="space-y-8">
                {essays.map((essay) => (
                  <Link
                    key={essay.essayId}
                    to="/essay/$essayId"
                    params={{ essayId: essay.essayId }}
                    className="group block"
                  >
                    <article className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
                      {/* Metin İçeriği (Sol Taraf) */}
                      <div className="md:col-span-2">
                        <h2 className="font-bold text-2xl tracking-tight transition-colors group-hover:text-primary">
                          {essay.title}
                        </h2>
                        {(essay.metaDescription || essay.excerpt) && (
                          <p className="mt-3 line-clamp-2 text-muted-foreground">
                            {essay.metaDescription || essay.excerpt}
                          </p>
                        )}
                        <p className="mt-4 text-muted-foreground text-xs">
                          {formatDate(essay.publishedAt)} · {essay.readTime}
                        </p>
                      </div>

                      {/* Resim (Sağ Taraf) */}
                      {essay.coverImageUrl && (
                        <div className="h-40 w-full overflow-hidden rounded-md md:h-full">
                          <img
                            src={essay.coverImageUrl}
                            alt={essay.title}
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                      )}
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
